<template>
  <v-btn
    ref="buttonRef"
    :class="buttonClasses"
    :style="buttonStyles"
    :color="resolvedButtonColor"
    :outlined="props.outlined"
    :disabled="props.disabled"
    :ripple="props.ripple"
    :text="props.text"
    :fab="props.fab"
    :x-small="props.xSmall"
    :elevation="props.elevation"
    :type="props.buttonType"
    :icon="props.icon"
    :download="props.download"
    :to="props.to"
    :plain="props.plain"
    :href="resolvedHref"
    @click="handleClick"
  >
    <v-overlay
      v-if="props.showLoader"
      :opacity="normalizedLoaderConfig.opacity"
      absolute
    >
      <v-progress-circular
        indeterminate
        :color="normalizedLoaderConfig.color"
        :size="normalizedLoaderConfig.size"
      />
    </v-overlay>

    <slot />

    <v-icon
      v-if="resolvedPrependIcon"
      class="prepend-icon"
      :class="variantConfig.prependIconClass"
      :style="resolvedPrependIconStyle"
    >
      {{ resolvedPrependIcon }}
    </v-icon>

    <image-field
      v-if="resolvedSrc && !props.showLoader"
      :src="resolvedSrc"
      :class="'mr-1 universal-button__image ' + normalizedImageConfig.class"
      :max-width="normalizedImageConfig.maxWidth"
      :height="normalizedImageConfig.height"
      :width="normalizedImageConfig.width"
    />

    <span class="button-label">
      {{ resolvedLabel }}
    </span>

    <v-icon
      v-if="resolvedAppendIcon"
      :class="variantConfig.appendIconClass"
      :color="props.appendIconColor"
      :style="resolvedAppendIconStyle"
    >
      {{ resolvedAppendIcon }}
    </v-icon>

    <slot name="label" />

    <!-- Hover tooltip, opt-in through the `tooltip` prop. It's declared inside
         the button but bound to it via `activator`, so nothing wraps the button
         and no existing markup or layout changes. Vuetify detaches the tooltip
         content to the app root, so sitting nested here doesn't affect where it
         renders. Custom content goes in the `tooltip` slot. -->
    <v-tooltip
      v-if="hasTooltip"
      :activator="buttonEl"
      v-bind="tooltipProps"
    >
      <slot name="tooltip">
        <span>
          {{ tooltipText }}
        </span>
      </slot>
    </v-tooltip>
  </v-btn>
</template>

<script setup>
import { computed, onMounted, ref, useSlots } from 'vue'
import ImageField from '../fields/ImageField.vue'
import { freezeObject, isObject, isSet, isString } from '@/global/utils/utils'
import { useBreakpoint } from '@/global/composables/useBreakpoint'
import { useTranslate } from '@/global/composables/useTranslate'

const { isMobile } = useBreakpoint()
const { translate } = useTranslate()

const DEFAULT_LOADER_CONFIG = freezeObject({
  // fully transparent, so only the spinner is drawn over the button content -
  // a tinted overlay over the label looks like the text is selected
  opacity: 0,
  color: 'var(--v-primary-base)',
  size: 20
})

const DEFAULT_IMAGE_CONFIG = freezeObject({
  class: '',
  maxWidth: 18,
  height: 18,
  width: undefined
})

const TOOLTIP_DIRECTIONS = freezeObject(['top', 'bottom', 'left', 'right'])

// Room between the button and the bubble - the tail sticks out into it.
const TOOLTIP_BUBBLE_GAP = 0

const TOOLTIP_GAP_PROP_BY_DIRECTION = freezeObject({
  top: 'nudgeTop',
  bottom: 'nudgeBottom',
  left: 'nudgeLeft',
  right: 'nudgeRight'
})

const BUTTON_VARIANT_FALLBACK = freezeObject({
  buttonClass: 'primary',
  buttonColor: '',
  textColor: '',
  label: '',
  prependIcon: '',
  appendIcon: '',
  src: '',
  prependIconClass: '',
  appendIconClass: '',
  prependIconStyle: {},
  appendIconStyle: {},
  imageConfig: {}
})

const props = defineProps({
  type: {
    type: String,
    default: 'save'
  },
  label: {
    type: String,
    default: ''
  },
  customClass: {
    type: String,
    default: ''
  },
  appendIcon: {
    type: String,
    default: ''
  },
  prependIcon: {
    type: String,
    default: ''
  },
  color: {
    type: String,
    default: ''
  },
  outlined: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  ripple: {
    type: Boolean,
    default: false
  },
  text: {
    type: Boolean,
    default: false
  },
  fab: {
    type: Boolean,
    default: false
  },
  xSmall: {
    type: Boolean,
    default: false
  },
  elevation: {
    type: [Number, String, Object],
    default: 0
  },
  src: {
    type: String,
    default: ''
  },
  icon: {
    type: Boolean,
    default: false
  },
  fontColor: {
    type: String,
    default: ''
  },
  download: {
    type: Boolean,
    default: false
  },
  href: {
    type: String,
    default: ''
  },
  appendIconColor: {
    type: String,
    default: ''
  },
  appendIconStyle: {
    type: Object,
    default: () => ({})
  },
  prependIconStyle: {
    type: Object,
    default: () => ({})
  },
  to: {
    type: [String, Object],
    default: undefined
  },
  plain: {
    type: Boolean,
    default: false
  },
  showLoader: {
    type: Boolean,
    default: false
  },
  loaderConfig: {
    type: Object,
    default: () => ({})
  },
  imageConfig: {
    type: Object,
    default: () => ({})
  },
  buttonType: {
    type: String,
    default: 'button'
  },
  // Hover text. Either a plain string, or a config object:
  //   text          the text itself (skip it when using the `tooltip` slot)
  //   direction     'top' (default) | 'bottom' | 'left' | 'right'
  //   disabled      turns the tooltip off without removing the config
  //   bubble        speech-bubble look with a tail out of the button, on by
  //                 default; `false` falls back to Vuetify's plain tooltip
  // Any other key is passed straight to `v-tooltip`, so its whole API is
  // available (openDelay, closeDelay, color, maxWidth, contentClass, ...).
  // A disabled button takes no pointer events (Vuetify), so it shows no
  // tooltip either - wrap it yourself if a disabled button has to explain why.
  tooltip: {
    type: [String, Object],
    default: ''
  }
})

const emit = defineEmits([
  'click'
])

const slots = useSlots()

const isMinimalVariant = computed(() => props.text || props.plain)

const variantConfig = computed(() => {
  switch (props.type) {
    case 'edit':
      return {
        ...BUTTON_VARIANT_FALLBACK,
        buttonClass: 'edit-button',
        buttonColor: props.color || '#65AD45',
        textColor: props.color || '#65AD45',
        label: translate('base.edit'),
        // The image (white pencil, sized for this button) replaces the mdi
        // icon - same treatment `edit-white` already gets from its own asset.
        src: props.src || '/img/icons/edit-button-icon.svg',
        // Rendered at the asset's own 17x17, so it isn't scaled.
        imageConfig: { maxWidth: 17, width: 17, height: 17 }
      }
    case 'edit-white':
      return {
        ...BUTTON_VARIANT_FALLBACK,
        buttonClass: isMobile.value ? 'base-button--mobile' : 'base-button',
        buttonColor: props.color || '#FFFFFF',
        textColor: props.fontColor || '#111827',
        label: translate('base.edit'),
        src: props.src || '/img/icons/edit-action-icon.svg'
      }
    case 'cancel':
      return {
        ...BUTTON_VARIANT_FALLBACK,
        buttonClass: 'cancel-button',
        buttonColor: props.color || '#EF4444',
        textColor: props.color || '#EF4444',
        label: translate('base.cancel')
      }
    case 'cancel-dialog':
      return {
        ...BUTTON_VARIANT_FALLBACK,
        buttonClass: 'cancel-dialog-button',
        buttonColor: props.color || '',
        textColor: props.fontColor || props.color || '#111827',
        label: translate('base.cancel')
      }
    case 'save':
      return {
        ...BUTTON_VARIANT_FALLBACK,
        buttonClass: 'save-button',
        buttonColor: props.color || '#1F2937',
        textColor: props.fontColor || props.color || '#1F2937',
        label: translate('base.save')
      }
    case 'green-save':
      return {
        ...BUTTON_VARIANT_FALLBACK,
        buttonClass: 'save-button',
        buttonColor: props.color || '#65AD45',
        textColor: props.fontColor || props.color || '#65AD45',
        label: translate('base.save')
      }
    case 'new':
      return {
        ...BUTTON_VARIANT_FALLBACK,
        buttonClass: 'new-button',
        buttonColor: props.color || '#65AD45',
        textColor: props.fontColor || props.color || '#65AD45',
        label: !isMobile.value ? translate('base.new') : '',
        src: props.src || '/img/icons/new-plus-icon.svg'
      }
    case 'delete-icon':
      return {
        ...BUTTON_VARIANT_FALLBACK,
        buttonClass: '',
        buttonColor: props.color || 'inherit',
        textColor: props.fontColor || props.color || '#EF4444',
        prependIcon: 'mdi-delete',
        prependIconClass: 'delete-icon'
      }
    case 'delete-action':
      return {
        ...BUTTON_VARIANT_FALLBACK,
        buttonClass: 'no-background-hover',
        buttonColor: props.color || 'inherit',
        src: props.src || '/img/icons/delete-action-red-icon.svg'
      }
    case 'link':
      return {
        ...BUTTON_VARIANT_FALLBACK,
        buttonClass: '',
        buttonColor: props.color || '#65AD45',
        textColor: props.fontColor || props.color || '#65AD45'
      }
    case 'close-icon':
      return {
        ...BUTTON_VARIANT_FALLBACK,
        buttonClass: 'no-background-hover',
        buttonColor: props.color || '#6B7280',
        textColor: props.fontColor || props.color || '#6B7280',
        prependIcon: 'mdi-close',
        prependIconClass: 'close-icon'
      }
    case 'edit-icon':
      return {
        ...BUTTON_VARIANT_FALLBACK,
        buttonClass: 'edit-icon-button',
        buttonColor: props.color || '',
        textColor: props.fontColor || props.color || '#111827',
        prependIcon: 'mdi-pencil'
      }
    case 'upload-file':
      return {
        ...BUTTON_VARIANT_FALLBACK,
        buttonClass: 'upload-file-button',
        buttonColor: props.color || '#65AD45',
        textColor: props.fontColor || props.color || '#65AD45',
        label: translate('base.edit'),
        prependIconClass: 'edit-prepend-icon'
      }
    case 'base':
      return {
        ...BUTTON_VARIANT_FALLBACK,
        buttonClass: isMobile.value ? 'base-button--mobile' : 'base-button',
        buttonColor: props.color !== undefined ? (props.color || '#FFFFFF') : '#FFFFFF',
        textColor: props.fontColor || props.color || '#111827',
        prependIconClass: 'base-prepend-icon',
        appendIconClass: isMobile.value ? 'base-append-icon--mobile' : 'base-append-icon'
      }
    case 'back':
      return {
        ...BUTTON_VARIANT_FALLBACK,
        buttonClass: isMobile.value ? 'back-button--mobile' : 'back-button',
        buttonColor: props.color ?? '#F3F4F6',
        textColor: props.fontColor || props.color || '#111827',
        appendIcon: 'mdi-chevron-left',
        appendIconClass: isMobile.value ? 'back-append-icon--mobile' : 'back-append-icon'
      }
    default:
      return {
        ...BUTTON_VARIANT_FALLBACK,
        buttonColor: props.color || ''
      }
  }
})

const buttonClasses = computed(() => {
  return [
    variantConfig.value.buttonClass,
    props.customClass,
    {
      'has-font-color': Boolean(props.fontColor)
    },
    'common'
  ].filter(Boolean)
})

const resolvedButtonColor = computed(() => variantConfig.value.buttonColor)

const resolvedLabel = computed(() => {
  return props.label || variantConfig.value.label
})

const resolvedPrependIcon = computed(() => {
  return props.prependIcon || variantConfig.value.prependIcon
})

const resolvedAppendIcon = computed(() => {
  return props.appendIcon || variantConfig.value.appendIcon
})

const resolvedSrc = computed(() => {
  return props.src || variantConfig.value.src
})

const resolvedPrependIconStyle = computed(() => {
  return Object.keys(props.prependIconStyle).length
    ? props.prependIconStyle
    : variantConfig.value.prependIconStyle
})

const resolvedAppendIconStyle = computed(() => {
  return Object.keys(props.appendIconStyle).length
    ? props.appendIconStyle
    : variantConfig.value.appendIconStyle
})

const normalizedLoaderConfig = computed(() => ({
  opacity: props.loaderConfig.opacity ?? DEFAULT_LOADER_CONFIG.opacity,
  color: props.loaderConfig.color || DEFAULT_LOADER_CONFIG.color,
  size: props.loaderConfig.size ?? DEFAULT_LOADER_CONFIG.size
}))

// A caller's `imageConfig` wins, then whatever the variant asks for (some
// assets have their own natural size), then the shared default.
const normalizedImageConfig = computed(() => {
  const variantImageConfig = variantConfig.value.imageConfig || {}

  return {
    class: props.imageConfig.class || variantImageConfig.class || DEFAULT_IMAGE_CONFIG.class,
    maxWidth: props.imageConfig.maxWidth ?? variantImageConfig.maxWidth ?? DEFAULT_IMAGE_CONFIG.maxWidth,
    height: props.imageConfig.height ?? variantImageConfig.height ?? DEFAULT_IMAGE_CONFIG.height,
    width: props.imageConfig.width ?? variantImageConfig.width ?? DEFAULT_IMAGE_CONFIG.width
  }
})

const resolvedHref = computed(() => {
  return props.download && props.href ? props.href : undefined
})

const buttonStyles = computed(() => {
  const contentColor = props.fontColor || (
    isMinimalVariant.value
      ? (props.color || variantConfig.value.textColor || resolvedButtonColor.value)
      : ''
  )

  return contentColor
    ? { '--universal-button-text-color': contentColor }
    : {}
})

const handleClick = () => {
  emit('click', props.type)
}

// --- Tooltip ---
// The button element itself is the tooltip's activator. Resolved in `onMounted`
// (not straight from the template ref) so the value is set after the nested
// tooltip has mounted - that assignment is what makes Vuetify bind its hover
// listeners to the button.
const buttonRef = ref(null)
const buttonEl = ref(null)

onMounted(() => {
  buttonEl.value = buttonRef.value?.$el || null
})

const tooltipConfig = computed(() => {
  if (isString(props.tooltip)) {
    return { text: props.tooltip }
  }
  return isSet(props.tooltip) && isObject(props.tooltip) ? props.tooltip : {}
})

const tooltipText = computed(() => {
  const { text } = tooltipConfig.value
  return isString(text) ? text : ''
})

const hasTooltipSlot = computed(() => !!slots.tooltip)

const hasTooltip = computed(() =>
  !tooltipConfig.value.disabled && (!!tooltipText.value || hasTooltipSlot.value)
)

const tooltipDirection = computed(() => {
  const { direction } = tooltipConfig.value
  return TOOLTIP_DIRECTIONS.includes(direction) ? direction : 'top'
})

// The speech-bubble look (a tail growing out of the button) is the default; a
// caller wanting Vuetify's plain tooltip passes `bubble: false`.
const isBubbleTooltip = computed(() => tooltipConfig.value.bubble !== false)

const tooltipProps = computed(() => {
  const { text, direction, bubble, contentClass, ...rest } = tooltipConfig.value
  const dir = tooltipDirection.value

  // Pushes the bubble away from the button in whichever direction it opens,
  // leaving the gap the tail sticks out into.
  const gap = isBubbleTooltip.value
    ? { [TOOLTIP_GAP_PROP_BY_DIRECTION[dir]]: TOOLTIP_BUBBLE_GAP }
    : {}

  const classes = [
    isBubbleTooltip.value && `universal-button-tooltip universal-button-tooltip--${dir}`,
    contentClass
  ].filter(Boolean).join(' ')

  return {
    top: dir === 'top',
    bottom: dir === 'bottom',
    left: dir === 'left',
    right: dir === 'right',
    contentClass: classes || undefined,
    ...gap,
    // Last, so a caller can still override the direction flags, the nudge or
    // anything else with the raw v-tooltip props.
    ...rest
  }
})
</script>

<style scoped>
.common {
  position: relative;
  letter-spacing: 0 !important;
  font-size: 12px !important;
  font-family: 'Satoshi', sans-serif !important;
}

/* The image is a v-img (a block that would otherwise stretch to the row height)
   and the label's line box is taller than its glyphs - pin both to the vertical
   center so the text sits on the icon's middle, whatever the button height. */
.common ::v-deep .v-btn__content {
  align-items: center;
}

.universal-button__image {
  align-self: center;
  flex: 0 0 auto;
}

.button-label {
  display: inline-flex;
  align-items: center;
  /* Optical, not geometric, centering: flex centers the label's *box*, and that
     box reserves descender space a short label ("Uredi", "Novo", ...) never
     uses, which leaves the glyphs sitting above the icon's middle. The extra
     top padding grows the box by 1px, and since the box stays centered the
     glyphs move down half of that - exactly the offset being corrected. */
  padding-top: 1px;
}

.common.v-btn--text,
.common.v-btn--plain,
.common.has-font-color {
  color: var(--universal-button-text-color, inherit) !important;
}

.common.v-btn--text,
.common.v-btn--plain {
  border-color: transparent !important;
}

.edit-button {
  color: #FFFFFF !important;
  font-size: 12px;
  font-weight: 500;
  border-radius: 8px;
  text-transform: none;
  height: 35px !important;
}

.upload-file-button {
  color: #FFFFFF !important;
  font-size: 12px;
  font-weight: 500;
  border-radius: 8px;
  text-transform: none;
}

.cancel-button {
  color: #FFFFFF !important;
  font-size: 12px;
  font-weight: 500;
  border-radius: 8px;
  text-transform: none;
  height: 35px !important;
}

.cancel-dialog-button {
  color: #111827;
  font-size: 12px;
  font-weight: 500;
  border-radius: 8px;
  border: 1px solid #E5E7EB;
  text-transform: none;
}

.save-button {
  color: #FFFFFF !important;
  font-size: 12px;
  font-weight: 500;
  border-radius: 8px;
  text-transform: none;
  height: 35px !important;
}

.new-button {
  color: white !important;
  font-size: 12px;
  height: 35px !important;
  font-weight: 500;
  border-radius: 8px;
  text-transform: none;
}

.base-button {
  color: #111827;
  font-size: 12px !important;
  font-weight: 500 !important;
  border-radius: 8px;
  border: 1px solid #E5E7EB !important;
  text-transform: none;
  height: 35px !important;
}

.base-button--mobile {
  color: #111827;
  font-size: 10px;
  font-weight: 500;
  border-radius: 8px;
  border: 1px solid #E5E7EB !important;
  text-transform: none;
  height: 35px !important;
}

.back-button {
  color: #111827;
  font-size: 12px;
  font-weight: 500;
  border-radius: 8px;
  border: 1px solid #E5E7EB !important;
  text-transform: none;
  height: 40px !important;
  width: 40px !important;
  min-width: unset !important;
}

.back-button--mobile {
  color: #111827;
  font-size: 10px;
  font-weight: 500;
  border-radius: 8px;
  border: 1px solid #E5E7EB !important;
  text-transform: none;
  height: 35px !important;
}

.delete-icon {
  color: #EF4444 !important;
}

.edit-icon-button {
  background-color: #E5E7EB;
}

.refresh-icon-button {
  color: #111827;
  font-size: 12px;
  font-weight: 500;
  border-radius: 8px;
  border: 1px solid #E5E7EB !important;
}

.close-icon {
  color: white !important;
  background: #6B7280;
}

/* The icon sits directly on the button - no white plate behind it, so it reads
   as an icon rather than a badge. That's also why it's white now: on the green
   button, the previous green-on-white pairing has nothing left to sit on. */
.edit-prepend-icon {
  color: #FFFFFF !important;
  width: 17px;
  height: 17px;
  margin-right: 8px;
}

.base-append-icon {
  font-size: 18px !important;
  color: #6B7280;
}

.base-append-icon--mobile {
  font-size: 16px !important;
}

.back-append-icon {
  font-size: 24px !important;
  color: #6B7280 !important;
}

.back-append-icon--mobile {
  font-size: 16px !important;
  color: #6B7280 !important;
}

.common.v-btn--text .base-prepend-icon,
.common.v-btn--plain .base-prepend-icon,
.common.v-btn--text .base-append-icon,
.common.v-btn--plain .base-append-icon,
.common.v-btn--text .base-append-icon--mobile,
.common.v-btn--plain .base-append-icon--mobile,
.common.v-btn--text .back-append-icon,
.common.v-btn--plain .back-append-icon,
.common.v-btn--text .back-append-icon--mobile,
.common.v-btn--plain .back-append-icon--mobile {
  color: var(--universal-button-text-color, #111827) !important;
}

.base-prepend-icon {
  color: #6B7280;
  margin-right: 6px;
}

.prepend-icon {
  font-family: 'Satoshi', sans-serif;
  font-size: 18px;
  margin-right: 4px;
  font-weight: 500;
}

@media (max-width: 600px) {
  .edit-button, .cancel-button, .save-button {
    height: 40px !important;
  }
}
</style>

<!-- Not scoped: Vuetify detaches the tooltip content to the app root, so a
     scoped selector would never reach it. Everything here is namespaced under
     `.universal-button-tooltip`, a class only this component sets, and only
     when the bubble look is on (`tooltip: { bubble: false }` opts out). -->
<style>
.v-tooltip__content.universal-button-tooltip {
  background: #1F2937;
  color: #FFFFFF;
  border-radius: 8px;
  padding: 6px 10px;
  font-family: 'Satoshi', sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  box-shadow: 0 4px 12px rgba(17, 24, 39, 0.18);
}

/* Vuetify fades tooltips in to .9 opacity - the bubble carries its own shading
   instead, and a see-through tail over the button reads as a rendering glitch.
   Scoped to the active state so the hidden state keeps its `opacity: 0`. */
.v-tooltip__content.universal-button-tooltip.menuable__content__active {
  opacity: 1 !important;
}

/* The tail: a small rotated square that inherits the bubble's background, so a
   caller-supplied `color` keeps body and tail the same shade. */
.universal-button-tooltip::after {
  content: '';
  position: absolute;
  width: 10px;
  height: 10px;
  background: inherit;
  border-radius: 2px;
  transform: rotate(45deg);
}

/* Placed on the edge facing the button, centered on it - which is where Vuetify
   anchors the bubble (horizontally for top/bottom, vertically for left/right). */
.universal-button-tooltip--top::after {
  bottom: -4px;
  left: 50%;
  margin-left: -5px;
}

.universal-button-tooltip--bottom::after {
  top: -4px;
  left: 50%;
  margin-left: -5px;
}

.universal-button-tooltip--left::after {
  right: -4px;
  top: 50%;
  margin-top: -5px;
}

.universal-button-tooltip--right::after {
  left: -4px;
  top: 50%;
  margin-top: -5px;
}
</style>
